import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../styles/colors';

/**
 * Composant pour protéger les routes nécessitant une authentification
 * @param {React.ReactNode} children - Composants enfants à afficher si authentifié
 * @param {string} requiredRole - Rôle requis pour accéder à la route (optionnel)
 * @param {string} resource - Ressource nécessitant une permission (optionnel)
 * @param {string} action - Action nécessitant une permission (optionnel)
 */
function ProtectedRoute({ children, requiredRole = null, resource = null, action = 'read' }) {
  const { isAuthenticated, isLoading, /*user,*/ hasPermission, hasRole } = useAuth();
  const location = useLocation();
  
  // Afficher un loader pendant la vérification de l'authentification
  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Chargement...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Rediriger vers la page de connexion si non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Vérifier le rôle requis si spécifié
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorBox}>
          <h2 style={styles.errorTitle}>Accès refusé</h2>
          <p style={styles.errorMessage}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <button
            onClick={() => window.history.back()}
            style={styles.backButton}
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  // Vérifier la permission si ressource et action spécifiées
  if (resource && !hasPermission(action, resource)) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorBox}>
          <h2 style={styles.errorTitle}>Accès refusé</h2>
          <p style={styles.errorMessage}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette ressource.
          </p>
          <button
            onClick={() => window.history.back()}
            style={styles.backButton}
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  // Utilisateur authentifié et autorisé
  return <>{children}</>;
}

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: colors.secondary.lightGray,
    fontFamily: '"Noto Sans", sans-serif'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: `4px solid ${colors.secondary.mediumGray}`,
    borderTop: `4px solid ${colors.primary.teal}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '1rem',
    fontSize: '1rem',
    color: colors.primary.gray
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: colors.secondary.lightGray,
    padding: '1rem',
    fontFamily: '"Noto Sans", sans-serif'
  },
  errorBox: {
    backgroundColor: colors.text.white,
    padding: '3rem',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    maxWidth: '500px'
  },
  errorTitle: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: colors.primary.gray,
    marginTop: 0,
    marginBottom: '1rem'
  },
  errorMessage: {
    fontSize: '1rem',
    color: colors.secondary.mediumGray,
    marginBottom: '2rem',
    lineHeight: '1.5'
  },
  backButton: {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.text.white,
    backgroundColor: colors.primary.teal,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: '"Noto Sans", sans-serif',
    transition: 'all 0.2s ease'
  }
};

export default ProtectedRoute;
